import { useState, useEffect, useCallback, useRef } from 'react';
import { checkProximityThreshold, Coordinates, SuperchargerWithDistance } from '../utils/distanceUtils';

export interface ProximityNotification {
  id: string;
  supercharger: SuperchargerWithDistance;
  restaurants: any[];
  createdAt: number;
}

export interface ProximityNotificationState {
  notifications: ProximityNotification[];
  waitUntil: number | null; // Timestamp when notifications should resume
  isDisabled: boolean; // Permanently disabled
  triggeredChargers: Set<string>; // Track which chargers have triggered notifications
}

export const useProximityNotifications = (
  userLocation: Coordinates | null,
  superchargers: any[],
  isTabletOrDesktop: boolean, // Only show on tablet and desktop
  onNavigateToCharger?: (chargerId: string) => void // Callback for "Show Me" button
) => {
  const [notificationState, setNotificationState] = useState<ProximityNotificationState>({
    notifications: [],
    waitUntil: null,
    isDisabled: false,
    triggeredChargers: new Set<string>()
  });

  const fetchRestaurantsRef = useRef<((chargerIdOrObject: string | any) => Promise<any[]>) | null>(null);
  const isProcessingRef = useRef(false);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Set the restaurant fetching function
  const setRestaurantFetcher = useCallback((fetcher: (chargerIdOrObject: string | any) => Promise<any[]>) => {
    fetchRestaurantsRef.current = fetcher;
  }, []);

  // Internal function that does the actual proximity check
  const checkProximityInternal = useCallback(async () => {
    // Skip if notifications are disabled or we're in wait period
    if (notificationState.isDisabled || 
        (notificationState.waitUntil && Date.now() < notificationState.waitUntil)) {
      return;
    }

    // Skip if not on tablet or desktop
    if (!isTabletOrDesktop) {
      return;
    }

    // Skip if no user location or no superchargers
    if (!userLocation || !superchargers.length) {
      return;
    }

    // Check all superchargers within 10km for notifications
    const nearbyChargers = superchargers.filter(charger => {
      const chargerWithDistance = checkProximityThreshold(
        userLocation, 
        [charger], 
        10
      );
      return chargerWithDistance !== null;
    });


    // Group nearby chargers by location (within 500m of each other) to avoid duplicates
    const chargerGroups: any[][] = [];
    for (const charger of nearbyChargers) {
      let addedToGroup = false;
      for (const group of chargerGroups) {
        const groupCharger = group[0];
        const distance = Math.sqrt(
          Math.pow((charger.latitude - groupCharger.latitude) * 111320, 2) + 
          Math.pow((charger.longitude - groupCharger.longitude) * 111320, 2)
        );
        if (distance < 500) { // Within 500 meters
          group.push(charger);
          addedToGroup = true;
          break;
        }
      }
      if (!addedToGroup) {
        chargerGroups.push([charger]);
      }
    }

    // Track triggered locations in this execution cycle to prevent duplicates
    const triggeredInThisCycle = new Set<string>();
    
    // Process each group (only show one notification per location group)
    for (const group of chargerGroups) {
      // Use the first charger in the group as the representative
      const representativeCharger = group[0];
      
      // Create a group key based on location (rounded to avoid tiny differences)
      const locationKey = `${representativeCharger.latitude.toFixed(3)}_${representativeCharger.longitude.toFixed(3)}`;
      
      // Don't trigger again for locations we've already shown (in state)
      if (notificationState.triggeredChargers.has(locationKey)) {
        continue;
      }

      // Don't trigger if already triggered in this execution cycle
      if (triggeredInThisCycle.has(locationKey)) {
        continue;
      }

      // Don't add if already in notifications for this location
      const existingNotification = notificationState.notifications.some(n => 
        `${n.supercharger.latitude.toFixed(3)}_${n.supercharger.longitude.toFixed(3)}` === locationKey
      );
      if (existingNotification) {
        continue;
      }
      
      // Mark as triggered in this cycle
      triggeredInThisCycle.add(locationKey);

      console.log('🚨 Proximity notification triggered:', {
        charger: representativeCharger.name,
        location: userLocation,
        groupSize: group.length
      });

      // Fetch restaurants for this supercharger
      if (fetchRestaurantsRef.current) {
        try {
          const restaurants = await fetchRestaurantsRef.current(representativeCharger);
          const topRestaurants = restaurants.slice(0, 3); // Get top 3

          const chargerWithDistance = checkProximityThreshold(userLocation, [representativeCharger], 10);
          if (chargerWithDistance) {
            const newNotification: ProximityNotification = {
              id: `${representativeCharger.id}-${Date.now()}`,
              supercharger: chargerWithDistance,
              restaurants: topRestaurants,
              createdAt: Date.now()
            };

            setNotificationState(prev => {
              const newTriggeredChargers = new Set(prev.triggeredChargers);
              newTriggeredChargers.add(locationKey);
              return {
                ...prev,
                notifications: [...prev.notifications, newNotification],
                triggeredChargers: newTriggeredChargers
              };
            });
          }
        } catch (error) {
          console.error('Failed to fetch restaurants for proximity notification:', error);
        }
      }
    }
  }, [userLocation, superchargers, isTabletOrDesktop, notificationState]);

  // Debounced version to prevent rapid successive calls
  const checkProximity = useCallback(() => {
    // Clear any existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Prevent concurrent executions
    if (isProcessingRef.current) {
      console.log('🔄 Skipping checkProximity - already processing');
      return;
    }

    // Set a short debounce delay (100ms) to batch rapid calls
    debounceTimeoutRef.current = setTimeout(async () => {
      if (isProcessingRef.current) return; // Double-check before processing
      
      isProcessingRef.current = true;
      try {
        await checkProximityInternal();
      } finally {
        isProcessingRef.current = false;
      }
    }, 100);
  }, [checkProximityInternal]);

  // Dismiss specific notification
  const dismissNotification = useCallback((notificationId: string) => {
    setNotificationState(prev => ({
      ...prev,
      notifications: prev.notifications.filter(n => n.id !== notificationId)
    }));
  }, []);

  // Navigate to supercharger (Show Me button)
  const navigateToCharger = useCallback((chargerId: string) => {
    if (onNavigateToCharger) {
      onNavigateToCharger(chargerId);
    }
  }, [onNavigateToCharger]);

  // Wait 1 hour
  const waitOneHour = useCallback(() => {
    const waitUntil = Date.now() + (60 * 60 * 1000); // 1 hour from now
    setNotificationState(prev => ({
      ...prev,
      notifications: [], // Close all notifications
      waitUntil
    }));
    console.log('🔕 Proximity notifications paused for 1 hour until:', new Date(waitUntil));
  }, []);

  // Wait 2 hours
  const waitTwoHours = useCallback(() => {
    const waitUntil = Date.now() + (2 * 60 * 60 * 1000); // 2 hours from now
    setNotificationState(prev => ({
      ...prev,
      notifications: [], // Close all notifications
      waitUntil
    }));
    console.log('🔕 Proximity notifications paused for 2 hours until:', new Date(waitUntil));
  }, []);

  // Turn off notifications permanently
  const turnOffNotifications = useCallback(() => {
    setNotificationState(prev => ({
      ...prev,
      notifications: [], // Close all notifications
      isDisabled: true
    }));
    console.log('🔕 Proximity notifications permanently disabled');
    
    // Save to localStorage
    localStorage.setItem('proximityNotificationsDisabled', 'true');
  }, []);

  // Enable notifications (for testing or user preference reset)
  const enableNotifications = useCallback(() => {
    setNotificationState(prev => ({
      ...prev,
      isDisabled: false,
      waitUntil: null,
      lastTriggeredCharger: null
    }));
    localStorage.removeItem('proximityNotificationsDisabled');
    console.log('🔔 Proximity notifications enabled');
  }, []);

  // Check localStorage on mount
  useEffect(() => {
    const isDisabled = localStorage.getItem('proximityNotificationsDisabled') === 'true';
    if (isDisabled) {
      setNotificationState(prev => ({ ...prev, isDisabled: true }));
    }
  }, []);

  // Check proximity when user location changes
  useEffect(() => {
    checkProximity();
  }, [checkProximity]);

  // Clear wait period when it expires
  useEffect(() => {
    if (notificationState.waitUntil && Date.now() >= notificationState.waitUntil) {
      setNotificationState(prev => ({
        ...prev,
        waitUntil: null
      }));
      console.log('🔔 Proximity notifications wait period expired');
    }
  }, [notificationState.waitUntil]);

  return {
    notificationState,
    setRestaurantFetcher,
    dismissNotification,
    navigateToCharger,
    waitOneHour,
    waitTwoHours,
    turnOffNotifications,
    enableNotifications, // For testing/debugging
    checkProximity // Manual trigger for testing
  };
};